import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Users, Target, CheckCircle2, Activity, TrendingUp, Landmark } from 'lucide-react';

const formatCLP = (amount: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(amount || 0);

function withinRange(value?: string, from?: string, to?: string) {
  if (!value) return true;
  const day = value.slice(0, 10);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

function getPresetDates(preset: string) {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  if (preset === '30d') {
    const d = new Date(now); d.setDate(d.getDate() - 30);
    return { from: d.toISOString().split('T')[0], to };
  }
  if (preset === '90d') {
    const d = new Date(now); d.setDate(d.getDate() - 90);
    return { from: d.toISOString().split('T')[0], to };
  }
  return { from: '', to: '' };
}

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrgId } = useOrgStore();
  const [company, setCompany] = useState<any>(null);
  const [contacts, setContacts] = useState<any[]>([]);
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!currentOrgId || !id) return;
      setLoading(true);
      const [companyRes, contactsRes, oppsRes, actsRes] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get('/contacts'),
        api.get('/opportunities'),
        api.get('/activities'),
      ]);
      setCompany(companyRes.data);
      setContacts((contactsRes.data || []).filter((c: any) => c.company_id === id));
      setOpportunities((oppsRes.data || []).filter((o: any) => o.account_id === id));
      setActivities((actsRes.data || []).filter((a: any) => a.company_id === id || a.related_to === companyRes.data?.name));
      setLoading(false);
    };
    load().catch(() => setLoading(false));
  }, [currentOrgId, id]);

  const filtered = useMemo(() => {
    const fContacts = contacts.filter((item) => withinRange(item.created_at, from, to));
    const fOpps = opportunities.filter((item) => withinRange(item.created_at, from, to) || withinRange(item.updated_at, from, to) || withinRange(item.close_date, from, to));
    const fActs = activities.filter((item) => withinRange(item.created_at, from, to) || withinRange(item.due_date, from, to));
    return { fContacts, fOpps, fActs };
  }, [contacts, opportunities, activities, from, to]);

  const won = filtered.fOpps.filter((o) => o.stage === 'Cerrado ganado');
  const openOpps = filtered.fOpps.filter((o) => !['Cerrado ganado', 'Cerrado perdido'].includes(o.stage));
  const totalWon = won.reduce((sum, o) => sum + Number(o.total_bruto || o.amount || 0), 0);
  const totalPipeline = openOpps.reduce((sum, o) => sum + Number(o.total_bruto || o.amount || 0), 0);
  const conversion = filtered.fOpps.length ? Math.round((won.length / filtered.fOpps.length) * 100) : 0;
  const leadStage = (company?.lead_stage || company?.status || 'prospecto').toString();

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Cargando detalle de empresa...</div>;
  }

  if (!company) {
    return <div className="p-6 text-sm text-slate-500">Empresa no encontrada.</div>;
  }

  return (
    <div data-testid="company-detail-page" className="space-y-6">
      <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="h-1.5 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
        <div className="flex flex-col gap-4 p-5 md:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
          <Button asChild variant="outline" className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2">
            <Link to="/empresas"><ArrowLeft size={16} className="mr-2" /> Volver</Link>
          </Button>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A] text-balance break-words" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{company.name}</h1>
            <p className="text-sm text-[#475569] mt-1 break-words">{company.razon_social || company.giro || 'Resumen de la organizacion'}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge className="max-w-full truncate bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20">{company.rut || 'Sin RUT'}</Badge>
              <Badge className="max-w-full truncate bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]">{company.region || 'Sin región'} / {company.comuna || 'Sin comuna'}</Badge>
              <Badge className="max-w-full truncate bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20">Lead: {leadStage}</Badge>
            </div>
          </div>
          <div className="grid w-full grid-cols-3 gap-2 sm:gap-3 lg:w-auto">
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-3 sm:px-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Contactos</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{filtered.fContacts.length}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-3 sm:px-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Oportunidades</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{filtered.fOpps.length}</p>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-3 sm:px-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Ganadas</p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#0F172A]">{won.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {['30d', '90d'].map((preset) => (
          <Button key={preset} variant="outline" size="sm" className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0" onClick={() => { const d = getPresetDates(preset); setFrom(d.from); setTo(d.to); }}>
            {preset === '30d' ? '30 días' : '90 días'}
          </Button>
        ))}
        <Button variant="outline" size="sm" className="min-h-11 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:min-h-0" onClick={() => { setFrom(''); setTo(''); }}>
          Limpiar filtros
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="overflow-hidden bg-white/95 backdrop-blur"><div className="h-1 bg-[#2563EB]" /><CardContent className="p-5"><div className="flex items-center gap-3"><Users className="shrink-0 text-[#2563EB]" size={18} /><div className="min-w-0"><p className="truncate text-xs uppercase tracking-widest text-[#94A3B8]">Contactos</p><p className="text-2xl font-bold tabular-nums">{filtered.fContacts.length}</p></div></div></CardContent></Card>
        <Card className="overflow-hidden bg-white/95 backdrop-blur"><div className="h-1 bg-[#8B5CF6]" /><CardContent className="p-5"><div className="flex items-center gap-3"><Target className="shrink-0 text-[#8B5CF6]" size={18} /><div className="min-w-0"><p className="truncate text-xs uppercase tracking-widest text-[#94A3B8]">Oportunidades</p><p className="text-2xl font-bold tabular-nums">{filtered.fOpps.length}</p></div></div></CardContent></Card>
        <Card className="overflow-hidden bg-white/95 backdrop-blur"><div className="h-1 bg-[#22C55E]" /><CardContent className="p-5"><div className="flex items-center gap-3"><CheckCircle2 className="shrink-0 text-[#22C55E]" size={18} /><div className="min-w-0"><p className="truncate text-xs uppercase tracking-widest text-[#94A3B8]">Ventas cerradas</p><p className="text-2xl font-bold tabular-nums">{won.length}</p></div></div></CardContent></Card>
        <Card className="overflow-hidden bg-white/95 backdrop-blur"><div className="h-1 bg-[#F59E0B]" /><CardContent className="p-5"><div className="flex items-center gap-3"><TrendingUp className="shrink-0 text-[#F59E0B]" size={18} /><div className="min-w-0"><p className="truncate text-xs uppercase tracking-widest text-[#94A3B8]">Conversión</p><p className="text-2xl font-bold tabular-nums">{conversion}%</p></div></div></CardContent></Card>
      </div>

      <Card className="overflow-hidden bg-white/95 backdrop-blur">
        <div className="h-1 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
        <CardHeader>
          <CardTitle>Resumen de la empresa</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <p className="text-sm text-[#475569]">
              {company.name} tiene {filtered.fContacts.length} contactos relacionados, {filtered.fOpps.length} oportunidades en el periodo y {won.length} ventas cerradas. La lectura del ciclo sugiere que el lead debe pasar por captación, calificación y prospecto antes de transformarse en contacto y luego en oportunidad de venta.
            </p>
            <div className="p-4 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
              <p className="text-xs uppercase tracking-widest text-[#94A3B8] mb-2">Historia comercial</p>
              <p className="text-sm text-[#334155]">
                Esta vista consolida la historia comercial desde la generación del lead hasta el cierre de negocio. Sirve para ver quién fue el contacto asociado, qué oportunidades se movieron, cuántas terminaron ganadas o perdidas y cómo evoluciona la cuenta en el tiempo.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="p-4 rounded-lg border border-[#E2E8F0]"><p className="text-xs uppercase tracking-widest text-[#94A3B8]">Últimas ventas</p><p className="text-2xl font-bold tabular-nums">{formatCLP(totalWon)}</p></div>
            <div className="p-4 rounded-lg border border-[#E2E8F0]"><p className="text-xs uppercase tracking-widest text-[#94A3B8]">Pipeline activo</p><p className="text-2xl font-bold tabular-nums">{formatCLP(totalPipeline)}</p></div>
            <div className="p-4 rounded-lg border border-[#E2E8F0]"><p className="text-xs uppercase tracking-widest text-[#94A3B8]">Actividad</p><p className="text-2xl font-bold tabular-nums">{filtered.fActs.length}</p></div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-1 overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-[#2563EB]" />
          <CardHeader><CardTitle>Contactos asociados</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {filtered.fContacts.map((item) => (
              <div key={item.id} className="rounded-lg border border-[#E2E8F0] p-3 min-h-11 cursor-pointer transition-colors hover:bg-[#F8FAFC]" onClick={() => navigate('/contactos')}>
                <p className="font-medium text-[#0F172A] break-words">{item.name}</p>
                <p className="text-xs text-[#475569]">{item.position || 'Sin cargo'}</p>
                <p className="text-xs text-[#475569]">{item.email}</p>
                <p className="text-xs text-[#475569] mt-1">Relación: lead calificado &rarr; contacto &rarr; oportunidad</p>
              </div>
            ))}
            {filtered.fContacts.length === 0 && <p className="text-sm text-[#94A3B8]">Sin contactos en este periodo.</p>}
          </CardContent>
        </Card>
        <Card className="xl:col-span-1 overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-[#8B5CF6]" />
          <CardHeader><CardTitle>Oportunidades</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {filtered.fOpps.map((item) => (
              <div key={item.id} className="rounded-lg border border-[#E2E8F0] p-3 min-h-11 cursor-pointer transition-colors hover:bg-[#F8FAFC]" onClick={() => navigate(`/oportunidades/${item.id}`)}>
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 break-words font-medium text-[#0F172A]">{item.name}</p>
                <Badge variant="outline" className="shrink-0">{item.stage}</Badge>
              </div>
              <p className="text-xs tabular-nums text-[#475569]">{formatCLP(item.total_bruto || item.amount || 0)}</p>
              <p className="text-xs text-[#475569]">Cierre: {item.close_date || 'Sin fecha'}</p>
              <p className="text-xs text-[#475569]">Cuenta: {company.name} · Contacto: {item.contact_name || 'Sin contacto'}</p>
            </div>
            ))}
            {filtered.fOpps.length === 0 && <p className="text-sm text-[#94A3B8]">Sin oportunidades en este periodo.</p>}
          </CardContent>
        </Card>
        <Card className="xl:col-span-1 overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-[#22C55E]" />
          <CardHeader><CardTitle>Actividades</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {filtered.fActs.map((item) => (
              <div key={item.id} className="rounded-lg border border-[#E2E8F0] p-3 min-h-11 cursor-pointer transition-colors hover:bg-[#F8FAFC]" onClick={() => navigate('/actividades')}>
                <p className="font-medium text-[#0F172A] break-words">{item.subject}</p>
                <p className="text-xs text-[#475569]">{item.type} · {item.status}</p>
                <p className="text-xs text-[#475569]">{item.due_date || item.created_at || 'Sin fecha'}</p>
              </div>
            ))}
            {filtered.fActs.length === 0 && <p className="text-sm text-[#94A3B8]">Sin actividades en este periodo.</p>}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-[#F59E0B]" />
          <CardHeader><CardTitle>Ciclo lead a venta</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <div className="flex gap-3"><div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#2563EB]" /><p>Lead: entrada inicial de contacto.</p></div>
            <div className="flex gap-3"><div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#F59E0B]" /><p>Calificado: valida si existe fit comercial y oportunidad real.</p></div>
            <div className="flex gap-3"><div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#8B5CF6]" /><p>Prospecto / Contacto: se formaliza la relación con la empresa.</p></div>
            <div className="flex gap-3"><div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#22C55E]" /><p>Oportunidad / Venta: negociación y cierre.</p></div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden bg-white/95 backdrop-blur">
          <div className="h-1 bg-[#2563EB]" />
          <CardHeader>
            <CardTitle>Filtro de fechas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#94A3B8] mb-1">Desde</p>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-[#94A3B8] mb-1">Hasta</p>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#475569]">
              <Calendar size={16} />
              Ajusta el rango para ver actividad histórica y ventas recientes.
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-hidden bg-white/95 backdrop-blur">
        <div className="h-1 bg-gradient-to-r from-[#8B5CF6] via-[#F59E0B] to-[#22C55E]" />
        <CardHeader>
          <CardTitle>Línea de evolución de la cuenta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0]">
            <div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#2563EB]" />
            <div>
              <p className="font-medium text-[#0F172A]">Lead inicial</p>
              <p className="text-xs text-[#475569]">Ingreso por web, referido o LinkedIn.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0]">
            <div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#F59E0B]" />
            <div>
              <p className="font-medium text-[#0F172A]">Calificación y contacto</p>
              <p className="text-xs text-[#475569]">Se valida fit comercial y se crea el contacto formal.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0]">
            <div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#8B5CF6]" />
            <div>
              <p className="font-medium text-[#0F172A]">Oportunidad</p>
              <p className="text-xs text-[#475569]">Negociación, propuesta y seguimiento.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0]">
            <div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#22C55E]" />
            <div>
              <p className="font-medium text-[#0F172A]">Venta / cierre</p>
              <p className="text-xs text-[#475569]">Negocio ganado y relación postventa.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
